<template>
  <q-item
    :data-test="CypressTarget('MySingleSeatItem')"
    multiline
  >
    <q-item-side>
      <q-item-tile
        icon="event_seat"
      />
    </q-item-side>
    <q-item-main>
      <q-item-tile
        :data-test="CypressTarget('Label')"
        label
      >
        {{ itemLabel }}
      </q-item-tile>
      <q-item-tile
        :data-test="CypressTarget('SubLabel')"
        sublabel
      >
        {{ itemSubLabel }}
      </q-item-tile>
      <q-item-tile
        v-if="gate"
        :data-test="CypressTarget('SubLabel')"
        sublabel
      >
        {{ gate }}
      </q-item-tile>
      <q-item-tile
        v-if="showPrice"
        :data-test="CypressTarget('Name')"
        sublabel
        style="margin-bottom: 5px"
      >
        {{ `${quantity > 1 ? `${quantity}x ` : ''}` }}{{ title }}
        <div
          v-if="description.length > 0"
          class="text-description"
        >
          <p v-if="!showFullDescription">
            {{ sortDescription }}
            <span v-if="fullDescription.length > 100">...
              <span
                class="read-more"
                @click="toggleShowFullDescription"
              >{{ $t('button.showMore') }}</span>
            </span>
          </p>
          <p v-else>
            {{ fullDescription }} <span
              class="read-more"
              @click="toggleShowFullDescription"
            >{{ $t('button.showLess') }}</span>
          </p>
        </div>
      </q-item-tile>
      <q-item-tile
        v-if="price > 0 && (extraAmounts.length > 0 || (showConvenienceFee && convenienceFee))"
        style="margin-top: 10px; margin-bottom: 5px; font-size: .8em"
      >
        <div class="d-flex flex-column text-muted mb-0 text-description text-grey-7">
          <div v-if="!newTaxesView">
            {{ $t('label.ticket') }}: {{ ticketPriceWithoutDiscount }}
          </div>
          <div
            v-if="hasCoupon && !newTaxesView"
            class="q-mt-xs"
          >
            {{ $t('label.couponDiscount') }}: -{{ formatMoney(discount) }}
          </div>
          <div
            v-for="(extra_amount, i_extra) in extraAmounts"
            :key="'extra'+i_extra"
            class="q-mt-xs"
          >
            {{ extra_amount.name+': '+formatMoney(extra_amount.amount) }}
          </div>
          <div
            v-if="showConvenienceFee && convenienceFee && !newTaxesView"
            class="q-mt-xs"
          >
            {{ $t('label.convenienceFee') }}: {{ formatMoney(convenienceFee) }}
          </div>
        </div>
      </q-item-tile>
      <q-item-tile
        v-else-if="hasCoupon && !newTaxesView"
        style="margin-top: 10px; margin-bottom: 5px; font-size: .8em"
      >
        <div class="d-flex flex-column text-muted mb-0 text-description text-grey-7">
          {{ $t('label.ticket') }}: {{ ticketPriceWithoutDiscount }}
          <div class="q-mt-xs">
            {{ $t('label.couponDiscount') }}: -{{ formatMoney(discount) }}
          </div>
        </div>
      </q-item-tile>
      <q-item-tile
        v-if="showPrice"
        :data-test="CypressTarget('Price')"
        :sublabel="priceAsSubLabel"
      >
        <div
          v-if="newTaxesView"
          class="ticket-price"
        >
          <coupon-tag
            v-bind="{
              price: price,
              hasTrueDiscount: hasTrueDiscount,
              isHiddenPrice: isHiddenPrice,
              hasCoupon: hasCoupon
            }"
          />
          <div>
            <div :class="hasCoupon && hasTrueDiscount ? 'has-coupon d-inline' : 'd-inline'">
              {{ ticketPriceWithoutDiscount }}
            </div>
            <div
              v-if="hasCoupon && hasTrueDiscount"
              class="d-inline q-ml-sm"
            >
              <i class="far fa-long-arrow-right" /> {{ ticketPrice }}
            </div>
          </div>
          <div
            v-if="showConvenienceFee && convenienceFee"
            class="q-mt-xs text-description"
          >
            (+ {{ formatMoney(convenienceFee) }} {{ $t('label.taxes') }})
          </div>
        </div>
        <div v-else>
          {{ totalPrice }}
        </div>
      </q-item-tile>
      <q-item-tile
        v-if="holder"
        :data-test="CypressTarget('HolderLabel')"
        sublabel
      >
        {{ seatHolderLabel ? $t('label.seatHolder') : $t('label.ticketHolder') }}
      </q-item-tile>
      <q-item-tile
        v-if="holder"
        :data-test="CypressTarget('Holder')"
        label
      >
        {{ holder }}
      </q-item-tile>
    </q-item-main>
    <q-item-side
      :data-test="CypressTarget('Buttons')"
      right
    >
      <q-item-side
        :data-test="CypressTarget('Buttons')"
        right
      >
        <button-remove
          :disable="loading"
          :loading="isLoading"
          @click="removeSeatAction"
        />
      </q-item-side>
    </q-item-side>
  </q-item>
</template>

<script>
  import formatMoney from 'helpers/formatters/money/index';
  import { PRICE_VISIBILITY_NEVER_SHOW } from '../../../helpers/constants/tickets';
  import CouponTag from '../../coupon/tag.vue';

  export default {
    name: 'ItemSeatBasicRemoveAction',
    components: {
      CouponTag,
      ButtonRemove: () => import('components/button/common/remove'),
    },
    props: {
      number: {
        type: [Number, String],
        required: false,
        default: '',
      },
      region: {
        type: [Number, String],
        required: false,
        default: '',
      },
      row: {
        type: [Number, String],
        required: false,
        default: '',
      },
      sector: {
        type: [Number, String],
        required: false,
        default: '',
      },
      gate: {
        type: [Number, String],
        required: false,
        default: '',
      },
      title: {
        type: [Number, String],
        required: false,
        default: '',
      },
      description: {
        type: [Number, String],
        required: false,
        default: '',
      },
      price: {
        type: [Number, String],
        required: false,
        default: '',
      },
      holder: {
        type: String,
        required: false,
        default: '',
      },
      showPrice: {
        type: Boolean,
        required: false,
        default: true,
      },
      seatHolderLabel: {
        type: Boolean,
        required: false,
        default: false,
      },
      quantity: {
        type: Number,
        required: false,
        default: 1,
      },
      priceAsSubLabel: {
        type: Boolean,
        required: false,
        default: false,
      },
      loading: {
        type: Boolean,
        required: false,
        default: false,
      },
      extraAmounts: {
        type: Array,
        default: () => [],
      },
      convenienceFee: {
        type: Number,
        default: null,
      },
      showConvenienceFee: {
        type: Boolean,
        default: false,
      },
      coupon: {
        type: Object,
        required: false,
        default: undefined,
      },
      discount: {
        type: Number,
        required: false,
        default: 0,
      },
    },
    data: () => ({
      showFullDescription: false,
      isLoading: false,
      openedCodeTip: false,
    }),
    computed: {
      itemLabel() {
        return `${this.$t('label.seat')} - ${this.row} ${this.number}`;
      },
      itemSubLabel() {
        return `${this.sector} ${this.region}`;
      },
      hasCoupon() {
        return this.coupon && (this.price > 0 || this.coupon?.price?.visibility === PRICE_VISIBILITY_NEVER_SHOW);
      },
      hasTrueDiscount() {
        if (this.coupon) {
          return this.coupon.amount !== this.coupon.original_amount;
        }
        return false;
      },
      isHiddenPrice() {
        if (!this.coupon) {
          return false;
        }
        return (this.coupon.price && ('visibility' in this.coupon.price))
          ? this.coupon.price.visibility === PRICE_VISIBILITY_NEVER_SHOW
          : false;
      },
      itemPrice() {
        return (this.price > 0)
          ? formatMoney(
            this.price,
            this.SET_LANGUAGE,
            this.SET_MONEY,
          ) : this.$t('label.freeSale');
      },
      ticketPrice() {
        if (this.hasCoupon) {
          if (this.coupon.amount > 0 && this.price > 0) {
            return this.formatMoney(this.price);
          }
          return this.$t('label.freeSale');
        }

        return this.ticketPriceWithoutDiscount;
      },
      ticketPriceWithoutDiscount() {
        if (this.coupon && this.coupon.original_amount && this.discount) {
          return this.formatMoney(this.price + this.discount);
        }
        if (this.price > 0) {
          return this.formatMoney(this.price);
        }

        return this.$t('label.freeSale');
      },
      totalPrice() {
        return (this.price > 0)
          ? formatMoney(
            parseFloat(this.price)
            + this.extraAmounts.reduce((t, e) => t + parseFloat(e.amount), 0)
            + (this.showConvenienceFee ? parseFloat(this.convenienceFee) : 0),
            this.SET_LANGUAGE,
            this.SET_MONEY,
          ) : this.$t('label.freeSale');
      },
      newTaxesView() {
        return this.SET_DISPLAY.taxes.enableNewTaxesView;
      },
      sortDescription() {
        if (this.description != null) {
          return this.description.substring(0, 100);
        }
        return '';
      },
      fullDescription() {
        return this.description !== null ? this.description : '';
      },
    },
    methods: {
      formatMoney(price) {
        return formatMoney(price, this.SET_LANGUAGE, this.SET_MONEY);
      },
      removeSeatAction() {
        this.isLoading = true;
        this.$emit('removeSeat');
        this.isLoading = false;
      },
      toggleShowFullDescription() {
        this.showFullDescription = !this.showFullDescription;
      },
    },
  };
</script>
<style lang="stylus" scoped>
  .text-description
    color: #757575 !important;
    font-weight: normal !important;
    font-size: 12px;
    overflow: hidden !important;
    hyphens: auto;
    span.read-more
      color: #027be3 !important;
      cursor: pointer;

  .ticket-price
    display: flex;
    flex-direction: row;
    align-items: baseline;
    gap: 6px;

  .text-description
    margin-top: 5px;

  .has-coupon
    color: red;
    text-decoration: line-through;

  .d-inline
    display: inline;

  .discount-applied
    display: block
    width: 100%
    position: relative;
    b:hover + p
      opacity: 1;
      z-index: 3;
    b
      background: #fff
      font-size: 12px;
      font-weight: 500;
      display: inline-block;
      padding: 5px;
      border-radius: 3px;
      color: var(--q-color-primary);
      border: 1px solid var(--q-color-primary);
      margin-bottom: 10px
      + p
        padding: 7px 10px;
        background-color: rgba(0,0,0,0.85);
        margin: 15px 0 0;
        position: absolute;
        z-index: -1;
        border-radius: 4px;
        color: #fff;
        font-weight: 500;
        font-size: 12px;
        line-height: 20px;
        transition: 0.15s ease opacity;
        opacity: 0;
        &:after
          width: 10px;
          height: 10px;
          background-color: inherit;
          border-radius: 0px;
          content: '';
          position: absolute;
          -webkit-clip-path: polygon(50% 0%, 100% 55%, 50% 55%, 0 55%);
          clip-path: polygon(50% 0%, 100% 55%, 50% 55%, 0 55%);
          top: -5px;
          left: 10px;
      i
        font-size: inherit !important;
    + .mobile-info
      width: 100%;
      .q-collapsible
        width: inherit;
        .q-collapsible-inner
          width: inherit;
          .q-item-division
            display: none;
          p
            font-size: 14px;
            margin: 7px 0 0;
</style>
